import { themeVar } from '@/styles/theme';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import Button from '@/components/basicComponents/button';
import { useClickOutside } from '../../../hooks/useClickOutside';
import CircleButtonRow from '../circleButtonRow';
import RedirectTitleCell from '../redirectTitleCell';

interface AnchorPopoverProps {
  iconButton?: React.ReactNode;
  children: React.ReactNode;
  rowBody?: React.ReactNode;
  title: string | any;
  nameCell?: string;
  hyperTitle?: () => void;
  limitNameCell?: boolean;
  className?: string;
  showLeft?: boolean;
  requestMakesInChildren?: boolean;
  onRedirect?: () => void;
  hoverControl?: boolean;
  showUnderContent?: boolean;
  showAboveContent?: boolean;
  zIndex?: number;
  isMobile?: boolean;
  mobileNotes?: boolean;
  isTwoColumn?: boolean;
  forceRecalculatePosition?: unknown;
}

interface DropdownPosition {
  top: number;
  left: number;
}

export const AnchorPopover = ({
  isMobile = false,
  rowBody,
  iconButton,
  title,
  nameCell,
  children,
  hyperTitle,
  showLeft = false,
  className,
  hoverControl,
  onRedirect,
  requestMakesInChildren = false,
  limitNameCell = false,
  showUnderContent = false,
  showAboveContent = false,
  zIndex = 100,
  mobileNotes = false,
  isTwoColumn = false,
  forceRecalculatePosition
}: AnchorPopoverProps) => {
  const windowWidth = window.innerWidth;
  const [isOpenDropDown, setIsOpenDropDown] = useState(false);
  const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const dropDownRef = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => handleCloseDropdown());

  const calculatePosition = useCallback(() => {
    if (!cellRef.current) return;

    const rect = cellRef.current.getBoundingClientRect();
    const dropdownWidth = isMobile ? windowWidth - 80 : isTwoColumn ? 680 : 440;
    const dropdownHeight = dropDownRef.current?.getBoundingClientRect().height || 400;
    const windowHeight = window.innerHeight;
    const HEADER_HEIGHT = 80;
    const PADDING = 10;

    let top: number;
    let left: number;

    if (isMobile) {
      // Mobile positioning
      top = rect.bottom + 6;
      left = mobileNotes ? rect.left : Math.max(PADDING, (windowWidth - dropdownWidth) / 2);
    } else if (showUnderContent) {
      // Show under the trigger element
      top = rect.bottom + 6;
      left = rect.left;
    } else if (showAboveContent) {
      // Show above the trigger element
      top = rect.top - dropdownHeight - 35;
      left = rect.left;
    } else {
      // Default positioning - to the side with vertical centering
      const availableHeight = windowHeight - HEADER_HEIGHT;
      const idealTop = rect.top + rect.height / 2 - dropdownHeight / 2;

      // Clamp top to stay within viewport
      top = Math.max(HEADER_HEIGHT + PADDING, Math.min(idealTop, windowHeight - dropdownHeight - PADDING));

      if (showLeft) {
        // Show to the left of the trigger
        left = rect.left - dropdownWidth - 16;
      } else {
        // Show to the right of the trigger
        left = rect.right + 16;
      }

      // Ensure dropdown stays within horizontal bounds
      if (left + dropdownWidth > windowWidth - PADDING) {
        left = rect.left - dropdownWidth - 16;
      }
      if (left < PADDING) {
        left = rect.right + 16;
      }
    }

    setPosition({ top, left });
  }, [isMobile, isTwoColumn, mobileNotes, showAboveContent, showLeft, showUnderContent, windowWidth]);

  useEffect(() => {
    if (isOpenDropDown) {
      calculatePosition();
      // Recalculate on scroll or resize
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);
      return () => {
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isOpenDropDown, calculatePosition]);

  // Recalculate position after dropdown renders to get accurate height
  useEffect(() => {
    if (isOpenDropDown && dropDownRef.current) {
      calculatePosition();
    }
  }, [isOpenDropDown, calculatePosition]);

  // Force recalculate position when forceRecalculatePosition prop changes
  useEffect(() => {
    if (isOpenDropDown && forceRecalculatePosition !== undefined) {
      calculatePosition();
    }
  }, [forceRecalculatePosition, isOpenDropDown, calculatePosition]);

  const handleCloseDropdown = () => {
    setIsOpenDropDown(false);
  };

  const handleDropdownChange = () => {
    setIsOpenDropDown(!isOpenDropDown);
  };

  const dropdownContent = (
    <AnimatePresence>
      {isOpenDropDown && (
        <PortalContainer ref={ref} $zIndex={zIndex}>
          <DropDown
            ref={dropDownRef}
            as={motion.div}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            $minWidth={isMobile ? windowWidth - 80 : isTwoColumn ? 680 : 440}
            className={className}
            style={{
              top: position.top,
              left: position.left
            }}
          >
            <HeaderDropDown>
              <DropDownHeader>
                {hyperTitle ? <HyperTitle onClick={hyperTitle}>{title}</HyperTitle> : <Title>{title}</Title>}
                <CloseInfoIconWrap onClick={() => handleCloseDropdown()}>
                  <CloseInfoIcon fill={themeVar('font.action')} />
                </CloseInfoIconWrap>
              </DropDownHeader>
              <Divider />
            </HeaderDropDown>
            {/* if request makes in children, we should render children only if isOpenDropDown is true*/}
            {requestMakesInChildren ? isOpenDropDown && children : children}
            {onRedirect && (
              <RedirectButton size="md" styleType="outline" onClick={onRedirect}>
                View {title}
              </RedirectButton>
            )}
          </DropDown>
        </PortalContainer>
      )}
    </AnimatePresence>
  );

  return (
    <Container onMouseLeave={() => hoverControl && handleCloseDropdown()}>
      <ToggleAnchor
        onClick={() => !hoverControl && handleDropdownChange()}
        ref={cellRef}
        onMouseEnter={() => hoverControl && setIsOpenDropDown(true)}
      >
        {nameCell && (
          <RedirectTitleCell id="actionCurioText" limit={limitNameCell}>
            {nameCell}
          </RedirectTitleCell>
        )}
        {iconButton && <CircleButtonRow>{iconButton}</CircleButtonRow>}
        {rowBody && rowBody}
      </ToggleAnchor>
      {ReactDOM.createPortal(dropdownContent, document.body)}
    </Container>
  );
};

const PortalContainer = styled.div<{ $zIndex: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: ${({ $zIndex }) => $zIndex};
`;

const Container = styled.div`
  width: 100%;
  position: relative;
  height: fit-content;
  margin: auto 0;

  &:hover {
    #infoWrap {
      background-color: #3ab070;

      svg {
        path {
          fill: #fff;
        }
      }
    }
  }
`;

const DropDown = styled.div<{
  $minWidth: number;
}>`
  min-width: ${({ $minWidth }) => `${$minWidth}px`};
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  position: fixed;
  background: ${themeVar('layer.base')};
  border: 1px solid ${themeVar('border.base')};
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.15);
  border-radius: 3px;
  padding: 20px 30px 30px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
`;

const HeaderDropDown = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-family: 'Blinker', serif;
  font-style: normal;
  font-weight: 600;
  font-size: 22px;
  word-spacing: 4px;
  line-height: 25px;
  color: ${themeVar('font.strong')};
  padding: 0 20px 0 0;
  margin: 0;
`;

const HyperTitle = styled.span`
  color: ${themeVar('font.action')};
  font-family: 'Blinker', serif;
  font-style: normal;
  font-weight: 600;
  word-spacing: 4px;
  line-height: 25px;
  font-size: 22px;
  padding: 0 20px 0 0;
  margin: 0;
  cursor: pointer;
`;

const DropDownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseInfoIconWrap = styled.div`
  cursor: pointer;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${themeVar('border.base')};
  margin-top: 10px;
  margin-bottom: 22px;
`;

const RedirectButton = styled(Button)`
  margin-top: 13px;
`;

const ToggleAnchor = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;
